import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  getTeacherCohort,
  getTeacherLessonDetail,
  getTeacherLessonTests,
  getTeacherSubmissionSnapshot,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { buildLoginPath } from "../auth/post-login-redirect";
import type { DemoTourStep } from "../student-ide/demoTourSteps";
import { StudentIdeBody, type LessonRuntimeMarkDoneSpec } from "../student-ide/StudentIdeBody";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function TeacherSubmissionRunPage() {
  const { cohortId, lessonId, attemptId } = useParams<{
    cohortId: string;
    lessonId: string;
    attemptId: string;
  }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const lessonTitle = searchParams.get("title") ?? "Lesson";
  const studentEmail = searchParams.get("student") ?? "student";
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [initialMainPy, setInitialMainPy] = useState("");
  const [tourSteps, setTourSteps] = useState<readonly DemoTourStep[] | undefined>(undefined);
  const [lessonTests, setLessonTests] = useState<readonly LessonRuntimeMarkDoneSpec[]>([]);

  const backTo = useMemo(() => {
    const qs = new URLSearchParams({ title: lessonTitle }).toString();
    return `/teacher/cohorts/${cohortId ?? ""}/lessons/${lessonId ?? ""}/submissions?${qs}`;
  }, [cohortId, lessonId, lessonTitle]);

  const storageKey = useMemo(
    () =>
      cohortId && lessonId && attemptId
        ? `ccs-teacher-run-submission-${cohortId}-${lessonId}-${attemptId}`
        : "ccs-teacher-run-submission",
    [cohortId, lessonId, attemptId]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (
        !bearer ||
        !cohortId ||
        !lessonId ||
        !attemptId ||
        !UUID_RE.test(cohortId) ||
        !UUID_RE.test(lessonId) ||
        !UUID_RE.test(attemptId)
      ) {
        setErr("Missing or invalid cohort, lesson, or submission id.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setErr(null);
      try {
        const cohort = await getTeacherCohort(bearer, cohortId);
        const courseId = cohort.cohort.courseId;
        const [detail, testsPayload, snapshot] = await Promise.all([
          getTeacherLessonDetail(bearer, courseId, lessonId),
          getTeacherLessonTests(bearer, courseId, lessonId),
          getTeacherSubmissionSnapshot(bearer, cohortId, lessonId, attemptId),
        ]);
        if (cancelled) return;

        const submitted =
          snapshot.snapshot?.mainPy?.trim() ||
          (typeof snapshot.snapshot?.mainPy === "string" ? snapshot.snapshot.mainPy : "");
        const fallback = detail.preview.mainPy?.trim() ? detail.preview.mainPy : 'print("Hello!")\n';
        setInitialMainPy(submitted || fallback);

        const sorted = [...testsPayload.tests].sort((a, b) => a.ordinal - b.ordinal || a.id.localeCompare(b.id));
        setLessonTests(
          sorted.map((t) => ({
            id: t.id,
            name: t.name,
            testCode: t.testCode ?? "",
            isRequired: t.isRequired,
            visibility: t.visibility,
            description: t.description ?? null,
          }))
        );

        const mapped: DemoTourStep[] = detail.tourSteps.map((t) => ({
          title: t.title,
          body: t.body.length > 0 ? t.body : ["(No body text for this step.)"],
          referenceCode: t.referenceCode ?? undefined,
        }));
        setTourSteps(
          mapped.length > 0
            ? mapped
            : [{ title: "Instructions", body: ["Follow the lesson directions while you run this student's code."] }]
        );
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Could not load submission for run");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, cohortId, lessonId, attemptId]);

  if (!bearer) {
    return (
      <main className="page">
        <div className="card">
          <p className="muted">Sign in as a teacher to run student submissions.</p>
          <Link to={buildLoginPath(`${location.pathname}${location.search}`)}>Log in</Link>
        </div>
      </main>
    );
  }

  if (err) {
    return (
      <main className="page">
        <div className="card card-wide">
          <div className="warn-banner" role="alert">
            {err}
          </div>
          <Link to={backTo} className="btn secondary">
            Back to submissions
          </Link>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="page">
        <div className="card">
          <p className="muted">Loading student submission…</p>
        </div>
      </main>
    );
  }

  return (
    <StudentIdeBody
      key={storageKey}
      eyebrow={`Teacher · Run as student · ${studentEmail}`}
      title={lessonTitle}
      backTo={backTo}
      backLabel="Submissions"
      initialMainPy={initialMainPy}
      storageKey={storageKey}
      tourSteps={tourSteps}
      lessonTestsForMarkDone={lessonTests}
    />
  );
}
